
import { openDB, IDBPDatabase } from 'idb';

const STORE_NAME = 'objects';
const DB_NAME = 'agro_cloud_storage';

let dbPromise: Promise<IDBPDatabase> | null = null;

const getDB = () => {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME);
        }
      },
    });
  }
  return dbPromise;
};

export const storageService = {
  /**
   * Uploads to authenticated, persistent server-side image storage.
   */
  async upload(file: Blob | File): Promise<string> {
    if (!navigator.onLine) throw new Error('Connect to the internet to upload a photo.');
    const data = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error('Cannot read image'));
      reader.onload = () => resolve(String(reader.result).split(',')[1]);
      reader.readAsDataURL(file);
    });
    const response = await fetch('/api/uploads', {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-Agro-Request': '1' },
      body: JSON.stringify({ data }),
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.message || 'Upload failed');
    return result.url;
  },

  /**
   * Simulates fetching a file from a CDN.
   */
  async download(url: string): Promise<Blob | null> {
    if (!url.startsWith('storage://')) return null;
    const id = url.replace('storage://', '');
    const db = await getDB();
    return await db.get(STORE_NAME, id);
  },

  /**
   * Deletes a file from storage.
   */
  async delete(url: string): Promise<void> {
    if (!url.startsWith('storage://')) return;
    const id = url.replace('storage://', '');
    const db = await getDB();
    await db.delete(STORE_NAME, id);
  },

  /**
   * Helper to determine if a URL is a storage reference.
   */
  isStorageUrl(url: string): boolean {
    return typeof url === 'string' && url.startsWith('storage://');
  }
};
